'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const steps = [
{
  number: '01',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>,

  title: 'Schedule in 30 Seconds',
  description:
  'Open the app, pick a pickup time, drop your bag outside your dorm door. No need to be home — we\'ll grab it.',
  detail: 'Available 7 days/week · Same-day slots open 6 AM',
  color: 'bg-primary/10 text-primary',
  accent: '#1A56DB',
  image:
  "https://img.rocket.new/generatedImages/rocket_gen_img_15271a71b-1772905511388.png",
  imageAlt: 'Student using a smartphone to schedule a laundry pickup'
},
{
  number: '02',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7v13a1 1 0 001 1h16a1 1 0 001-1V7" />
        <path d="M1 3h22v4H1z" />
        <path d="M10 12h4" />
      </svg>,

  title: 'We Wash, Dry & Fold',
  description:
  'Your clothes are washed with premium detergent, dried at the right temperature, and neatly folded — all within 24 hours.',
  detail: 'Hypoallergenic options · Separate colors & whites',
  color: 'bg-accent/10 text-accent',
  accent: '#38BDF8',
  image:
  "https://img.rocket.new/generatedImages/rocket_gen_img_171f0d786-1772218432514.png",
  imageAlt: 'Clean neatly folded laundry ready for delivery'
},
{
  number: '03',
  icon:
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>,

  title: 'Delivered Back to Your Dorm',
  description:
  'Fresh clothes land back at your door, neatly bagged. You get a text when they\'re on the way — no waiting required.',
  detail: 'Real-time tracking · Contactless delivery',
  color: 'bg-success/10 text-success',
  accent: '#22C55E',
  image:
  "https://img.rocket.new/generatedImages/rocket_gen_img_170407773-1773070769431.png",
  imageAlt: 'Delivery person dropping off a laundry bag at a dorm door'
}];


export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('hidden-before-reveal');
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    els?.forEach((el) => {
      el?.classList?.add('hidden-before-reveal');
      observer?.observe(el);
    });

    return () => observer?.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-32 bg-campusbg overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20 reveal stagger-1">
          <div className="inline-flex items-center gap-2 bg-primary/8 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-primary text-xs font-700 uppercase tracking-wider">How It Works</span>
          </div>
          <h2 className="font-display font-800 text-[clamp(36px,5vw,60px)] tracking-tight text-secondary leading-tight mb-4">
            Three steps to never visit a<br />
            <span className="text-primary">laundromat again</span>
          </h2>
          <p className="text-muted text-lg font-400 max-w-xl mx-auto">
            From bag at your door to clothes back in your closet — the whole thing takes less than 24 hours.
          </p>
        </div>

        {/* Steps — Asymmetric layout */}
        <div className="space-y-12">
          {steps?.map((step, i) =>
          <div
            key={step?.number}
            className={`reveal stagger-${i + 2} grid md:grid-cols-2 gap-8 items-center ${
            i % 2 === 1 ? 'md:[direction:rtl]' : ''}`
            }>
            
              {/* Image card */}
              <div className={`relative rounded-xl overflow-hidden shadow-float ${i % 2 === 1 ? 'md:[direction:ltr]' : ''}`}>
                <div className="aspect-[4/3] relative">
                  <AppImage
                  src={step?.image}
                  alt={step?.imageAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw" />
                
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-secondary/60 via-transparent to-transparent" />

                  {/* Step number badge */}
                  <div className="absolute top-6 left-6">
                    <span
                    className="text-7xl font-800 leading-none"
                    style={{ color: step?.accent, opacity: 0.9, textShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
                    
                      {step?.number}
                    </span>
                  </div>

                  {/* Detail pill */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3">
                      <p className="text-white text-xs font-600">{step?.detail}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content card */}
              <div className={`${i % 2 === 1 ? 'md:[direction:ltr]' : ''}`}>
                <div className="feature-card bg-white rounded-xl border border-border p-10 shadow-card h-full">
                  <div className={`w-14 h-14 rounded-xl ${step?.color} flex items-center justify-center mb-6`}>
                    {step?.icon}
                  </div>
                  <h3 className="font-display font-800 text-2xl text-secondary mb-4 tracking-tight">
                    {step?.title}
                  </h3>
                  <p className="text-muted text-base font-400 leading-relaxed">
                    {step?.description}
                  </p>

                  {/* Progress dots */}
                  <div className="flex items-center gap-2 mt-8">
                    {steps?.map((_, j) =>
                  <div
                    key={j}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                    j === i ?
                    'w-8 bg-primary' : 'w-2 bg-muted-light'}`
                    } />

                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}